type ShortTag = [string, string | ShortTag[]] | [];
type TagArrayable = string | Tag | Tag[] | ShortTag[] | null;

export default class Tag {
  #tag: string;
  #length: number;
  #value: string | Tag[];

  static getStringValue(tag: string, input: TagArrayable): string | undefined {
    const foundTag = this.toTagArray(input).find((t) => t.tag === tag);
    if (typeof foundTag?.value === "string") {
      return foundTag.value;
    }

    return undefined;
  }

  static from(tag: string, input: string | TagArrayable): Tag {
    let length: number;
    let value: string | Tag[];

    if (typeof input === "string") {
      value = input;
      length = Array.from(input).length;
    } else {
      value = this.toTagArray(input);
      length = value.reduce((acc, tag) => acc + tag.length + 4, 0);
    }

    return new this(tag, length, value);
  }

  static toString(input: TagArrayable): string {
    return this.toTagArray(input)
      .map((tag) => tag.toString())
      .join("");
  }

  static toTagArray(input?: TagArrayable): Tag[] {
    let tagList: Tag[] = [];

    if (typeof input === "string") {
      tagList = this.#parse(input);
    } else if (input instanceof Tag) {
      tagList.push(input);
    } else if (input != null) {
      if (input.every((value) => Array.isArray(value))) {
        input.forEach(([k, v]) => {
          if (k && v) {
            tagList.push(this.from(k, v));
          }
        });
      } else {
        tagList = input;
      }
    }

    return tagList;
  }

  static create(input: TagArrayable): string {
    const inputWithChecksumTag = [
      ...this.toTagArray(input),
      new this("63", 4, ""),
    ];
    let output = this.toString(inputWithChecksumTag);
    output += this.#calculateChecksum(output);

    if (Array.from(output).length > 512) {
      throw new Error("Payload exceeds 512 characters");
    }

    return output;
  }

  static debug(input: TagArrayable): void {
    this.traverse(input, (tag, path) => {
      const indent = "\t".repeat(path.length + 1);
      if (typeof tag.value === "string") {
        console.log(
          `${indent}${tag.tag} ${tag.length.toString(10)} ${tag.value}`,
        );
      } else {
        console.log(`${indent}${tag.tag} ${tag.length.toString(10)}`);
      }
    });
  }

  static traverse(
    input: TagArrayable,
    func: (tag: Tag, path: Tag[]) => void,
    path: Tag[] = [],
  ) {
    this.toTagArray(input).forEach((tag) => {
      func(tag, path);
      if (typeof tag.value !== "string") {
        this.traverse(tag.value, func, [...path, tag]);
      }
    });
  }

  static #parse(input?: string, path: string[] = []): Tag[] {
    const tags: Tag[] = [];

    if (input == null) {
      return tags;
    }

    const unicodeCodepoints = Array.from(input);

    let index = 0;
    let currentTag: string;
    let currentLength: number;
    let currentValue: string | Tag[];

    while (index < unicodeCodepoints.length) {
      currentTag = unicodeCodepoints.slice(index, index + 2).join("");
      currentLength = parseInt(
        unicodeCodepoints.slice(index + 2, index + 4).join(""),
        10,
      );
      currentValue = unicodeCodepoints
        .slice(index + 4, index + currentLength + 4)
        .join("");

      const newPath = [...path, currentTag];
      if (this.#isTemplate(newPath)) {
        currentValue = this.#parse(currentValue, newPath);
      }

      tags.push(new this(currentTag, currentLength, currentValue));

      index += currentLength + 4;
    }

    return tags;
  }

  static #isTemplate(path: string[]): boolean {
    const pathTags = path.map((p) => parseInt(p, 10));
    if (path.length === 1) {
      if (pathTags[0] >= 26 && pathTags[0] <= 51) {
        // Merchant Account Information
        return true;
      }

      if (pathTags[0] >= 80 && pathTags[0] <= 99) {
        // Unreserved Templates
        return true;
      }

      if (pathTags[0] === 62) {
        // Additional Data Field Template
        return true;
      }

      if (pathTags[0] === 64) {
        // Merchant Information Language Template
        return true;
      }
    } else if (path.length === 2) {
      if (pathTags[0] === 62 && pathTags[1] >= 50 && pathTags[1] <= 99) {
        // Payment System specific templates
        return true;
      }
    }

    return false;
  }

  static #calculateChecksum(input: string): string {
    let crc = 0xffff;
    const polynomial = 0x1021;
    const textEncoder = new TextEncoder();
    const bytes = textEncoder.encode(input); // The checksum is computed over the UTF-8 encoded string

    for (let i = 0; i < bytes.length; i++) {
      const byte = bytes[i];
      for (let j = 0; j < 8; j++) {
        const bit = ((byte >> (7 - j)) & 1) === 1;
        const c15 = ((crc >> 15) & 1) === 1;
        crc <<= 1;
        if (c15 !== bit) {
          crc ^= polynomial;
        }
      }
    }

    crc &= 0xffff;
    return crc.toString(16).toUpperCase().padStart(4, "0");
  }

  private constructor(tag: string, length: number, value: string | Tag[]) {
    this.#tag = tag;
    this.#length = length;
    this.#value = value;

    if (tag.length !== 2) {
      throw new Error("tag must be 2 digits");
    }

    if (length < 0 || length > 99) {
      throw new Error("length can only be 0 to 99");
    }
  }

  get tag() {
    return this.#tag;
  }

  get length() {
    return this.#length;
  }

  get value() {
    return this.#value;
  }

  toString() {
    let value: string;

    if (typeof this.#value === "string") {
      value = this.#value;
    } else {
      value = this.#value.map((tag) => tag.toString()).join("");
    }

    return `${this.#tag}${this.#length.toString(10).padStart(2, "0")}${value}`;
  }
}
